import { z } from 'zod';

export const credentialsSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address')
    .email('Enter a valid email address'),
  // Deliberately no strength rules here. They belong where a password is
  // chosen; applying them at sign-in only tells an attacker what the rules are.
  password: z.string().min(1, 'Enter your password'),
});

/**
 * The emailed code, as the backend accepts it: 4 to 10 digits. The backend
 * sends 4 today, but the length is its setting (MFA_CODE_LENGTH), so the
 * portal takes any length the backend would.
 */
const emailedCode = z
  .string()
  .min(1, 'Enter the code we emailed you')
  .regex(/^\d{4,10}$/, 'Enter the code from the email: 4 to 10 digits');

export const verificationSchema = z.object({
  code: emailedCode,
});

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address')
    .email('Enter a valid email address'),
});

/**
 * Matches what the backend enforces, so a password is never rejected twice.
 * The letter and digit checks are Unicode-aware, as Python's isupper(),
 * islower() and isdigit() are: an "Ä" is an upper case letter there too.
 */
const strongPassword = z
  .string()
  .min(8, 'Use at least 8 characters')
  .regex(/\p{Lu}/u, 'Include an upper case letter')
  .regex(/\p{Ll}/u, 'Include a lower case letter')
  .regex(/\p{Nd}/u, 'Include a digit');

export const resetPasswordSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Enter your email address')
      .email('Enter a valid email address'),
    code: emailedCode,
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Setting a password through an invitation link.
 *
 * The reset form minus the address and the code: the token in the link is
 * what identifies the person, so there is nothing else to ask for. It shares
 * `strongPassword` and the mismatch rule, so an invitation and a reset can
 * never disagree about what a good password is.
 */
export const acceptInvitationSchema = z
  .object({
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  });

export type CredentialsValues = z.infer<typeof credentialsSchema>;
export type VerificationValues = z.infer<typeof verificationSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;
export type AcceptInvitationValues = z.infer<typeof acceptInvitationSchema>;
